/**
 * The console Usage page, mounted in the Platform Management Console, the Partner Portal and the
 * Enterprise Portal. One component rather than three so the roles cannot drift apart.
 *
 * The table's top level is the level below the caller's own scope, taken from `report.scope`, the
 * server's echo of that scope rather than a role read locally.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '@/services/api';
import { csvRow } from '@/utils/csv';
import {
  SUBSCRIPTION_STATUS_LABELS,
  SUBSCRIPTION_STATUS_TONES,
  type SubscriptionStatusTone,
} from '@/utils/subscriptionStatus';
import { DataTable, ColumnDefinition, SortState } from '@/components/shared/DataTable';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  FileText,
  Network,
  RefreshCcw,
  Store,
  TrendingUp,
  User,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AnimatedCard } from '../ui/AnimatedCard';

interface UsageTotals {
  encounters: number;
  minutes: number;
  untypedRows: number;
}

/** What the plan included for the reported month. Absent is not zero, and reads as not recorded. */
interface PeriodAllowance {
  planName: string | null;
  encounters: number;
  minutes: number;
  clinicalUserCount: number;
}

/** A parent row's allowance, plus how much of the row it actually accounts for. */
interface AllowanceSubtotal {
  /** The metered allowances only; an unlimited one is counted rather than summed. */
  encounters: number;
  minutes: number;
  unlimitedEncounters: number;
  unlimitedMinutes: number;
  organizationsWithAllowance: number;
  organizations: number;
}

interface OrganizationSubscription {
  status: string;
  planName: string | null;
}

interface ProviderRollup {
  userId: string;
  name?: string;
  email?: string;
  npi?: string;
  identityResolved: boolean;
  lastActivityAt?: string;
  totals: UsageTotals;
}

/**
 * How far the organization went past its pool for the month: the per-clinical-user allowance times
 * its clinical users, against its metered usage. Computed by the server.
 */
interface UsageOverage {
  encounters: number;
  minutes: number;
}

interface OrganizationRollup {
  organizationId: string;
  name: string;
  /** The EHR tenant id, exported as PCID. */
  externalTenantId: string | null;
  isDeleted: boolean;
  totals: UsageTotals;
  allowance: PeriodAllowance | null;
  subscription: OrganizationSubscription | null;
  /** Null when there is no allowance to measure against. */
  overage: UsageOverage | null;
  providers: ProviderRollup[];
}

interface PartnerRollup {
  partnerId: string;
  name: string;
  cpid: string | null;
  enterpriseId: string | null;
  totals: UsageTotals;
  allowance: AllowanceSubtotal | null;
  organizations: OrganizationRollup[];
}

interface EnterpriseRollup {
  enterpriseId: string | null;
  name: string | null;
  totals: UsageTotals;
  allowance: AllowanceSubtotal | null;
  partners: PartnerRollup[];
}

type ConsoleScope =
  | { kind: 'platform' }
  | { kind: 'enterprise'; enterpriseId: string }
  | { kind: 'partner'; partnerId: string };

/**
 * What the caller may filter by. Wider than the tree, which narrows to a selected organization, so
 * the tree cannot also be the list of choices.
 */
interface UsageFilterOptions {
  enterprises: { enterpriseId: string; name: string; isDeleted: boolean }[];
  partners: { partnerId: string; name: string; enterpriseId: string | null; isDeleted: boolean }[];
  organizations: { organizationId: string; name: string; partnerId: string; isDeleted: boolean }[];
}

/**
 * A deactivated enterprise, partner or organization, at the highest level that is deactivated.
 * Its usage for the period happened and stays in the report's totals; it is shown apart from the
 * live tree.
 */
interface DeactivatedRollup {
  kind: 'enterprise' | 'partner' | 'organization';
  id: string;
  name: string;
  enterpriseName: string | null;
  partnerName: string | null;
  totals: UsageTotals;
  partners: { partnerId: string; name: string; cpid: string | null; organizations: OrganizationRollup[] }[];
}

interface UsageRollupReport {
  period: { key: string; from: string; to: string; basis: 'calendar-month' };
  scope: ConsoleScope;
  totals: UsageTotals;
  degraded: boolean;
  /** Encounters counted in more than one provider or organization bucket. */
  encountersSpanningBuckets?: number;
  /** The live tree. */
  enterprises: EnterpriseRollup[];
  deactivated?: DeactivatedRollup[];
  /** The part of `totals` that belongs to `deactivated`. */
  deactivatedTotals?: UsageTotals;
  available?: UsageFilterOptions;
}

interface Filters {
  periodKey: string;
  enterpriseId: string;
  partnerId: string;
  organizationId: string;
}

/** UTC so the month shown matches the month the server filtered on. */
function currentMonthKey(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

function monthOptions(count = 12): { key: string; label: string }[] {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const date = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const key = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
    return {
      key,
      label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
    };
  });
}

function monthLabel(periodKey: string): string {
  const [year, month] = periodKey.split('-').map(Number);
  if (!year || !month) return periodKey;
  return new Date(Date.UTC(year, month - 1, 1))
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

const UNASSIGNED_ENTERPRISE = 'No enterprise';
const NO_SUBSCRIPTION = 'No subscription';
const NOT_RECORDED = 'Not recorded';

/** red-800, the colour the console's Status column gives a deactivated row. */
const DEACTIVATED_OPTION_STYLE = { color: '#991b1b' };

interface FilterOption {
  id: string;
  name: string;
  isDeleted: boolean;
}

/**
 * Deactivated choices sit in their own group, after the live ones. The heading is what a screen
 * reader announces, so the colour is not carrying the meaning alone.
 *
 * All three filters render through this, because a deactivated enterprise and a deactivated
 * channel partner are as selectable, and as worth marking, as a deactivated organization.
 */
const FilterOptions: React.FC<{
  options: FilterOption[];
  allLabel: string;
}> = ({ options, allLabel }) => {
  const live = options.filter(o => !o.isDeleted);
  const deactivated = options.filter(o => o.isDeleted);

  return (
    <>
      <option value="">{allLabel}</option>
      {live.map(o => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
      {deactivated.length > 0 && (
        <optgroup label="Deactivated">
          {deactivated.map(o => (
            <option key={o.id} value={o.id} style={DEACTIVATED_OPTION_STYLE}>{o.name}</option>
          ))}
        </optgroup>
      )}
    </>
  );
};

/**
 * The export as a string, separate from the download so it can be asserted on.
 *
 * Every line carries its organization's allowance: a spreadsheet has no hierarchy to inherit it
 * from. The overage is the organization's, one figure per subscription, so it sits on the
 * organization's first line only and summing the column counts each organization once.
 */
export function buildUsageCsv(report: UsageRollupReport): string {
  const rows: string[] = [
    csvRow([
      'Month', 'Enterprise', 'CPID', 'Channel Partner', 'PCID', 'Organization',
      'Organization Deleted', 'Plan', 'Subscription Status', 'Included Encounters',
      'Included Minutes', 'Encounter Overage', 'Minutes Overage', 'Provider', 'Provider Email',
      'Provider NPI', 'Encounters', 'Minutes', 'Identity Resolved', 'Unclassified Records',
      'Last Activity', 'Deactivated',
    ]),
  ];

  // Live organizations, then the deactivated ones (theirs is part of the month's usage too). The
  // last column says whether the organization or anything above it is deactivated.
  const groups: {
    enterpriseName: string;
    partners: { name: string; cpid: string | null; organizations: OrganizationRollup[] }[];
    deactivated: boolean;
  }[] = [
    ...report.enterprises.map(e => ({ enterpriseName: e.name ?? UNASSIGNED_ENTERPRISE, partners: e.partners, deactivated: false })),
    ...(report.deactivated ?? []).map(d => ({
      enterpriseName: (d.kind === 'enterprise' ? d.name : d.enterpriseName) ?? UNASSIGNED_ENTERPRISE,
      partners: d.partners,
      deactivated: true,
    })),
  ];

  for (const enterprise of groups) {
    for (const partner of enterprise.partners) {
      for (const org of partner.organizations) {
        // A provider-less organization still gets a line, allowance included.
        const providers: (ProviderRollup | null)[] =
          org.providers.length > 0 ? org.providers : [null];
        providers.forEach((provider, index) => {
          const overage = index === 0 ? org.overage : null;
          rows.push(csvRow([
            report.period.key,
            enterprise.enterpriseName,
            partner.cpid ?? '',
            partner.name,
            org.externalTenantId ?? '',
            org.name,
            org.isDeleted ? 'Yes' : 'No',
            org.subscription?.planName ?? NO_SUBSCRIPTION,
            org.subscription?.status ?? '',
            org.allowance ? limitText(org.allowance.encounters) : NOT_RECORDED,
            org.allowance ? limitText(org.allowance.minutes) : NOT_RECORDED,
            overage ? overage.encounters : '',
            overage ? overage.minutes : '',
            provider ? (provider.identityResolved ? provider.name ?? '' : 'Unknown provider') : '',
            provider?.email ?? '',
            provider?.npi ?? '',
            provider ? provider.totals.encounters : org.totals.encounters,
            provider ? provider.totals.minutes : org.totals.minutes,
            provider ? (provider.identityResolved ? 'Yes' : 'No') : '',
            provider ? provider.totals.untypedRows : org.totals.untypedRows,
            provider?.lastActivityAt ?? '',
            enterprise.deactivated ? 'Yes' : 'No',
          ]));
        });
      }
    }
  }

  return rows.join('\n');
}

/** A negative included amount is how an unlimited plan is stored. */
function isUnlimited(limit: number): boolean {
  return limit < 0;
}

function limitText(limit: number): string {
  return isUnlimited(limit) ? 'Unlimited' : String(limit);
}

/** How much of the allowance is gone. An allowance of nothing that was used is entirely gone. */
function usedPercent(used: number, limit: number): number {
  if (isUnlimited(limit)) return 0;
  if (limit <= 0) return used > 0 ? 100 : 0;
  return (used / limit) * 100;
}

function fillColor(percent: number, limit: number): string {
  if (isUnlimited(limit)) return 'bg-emerald-500';
  if (percent >= 90) return 'bg-red-500';
  if (percent >= 75) return 'bg-amber-500';
  return 'bg-emerald-500';
}

const Allowance: React.FC<{ used: number; limit: number }> = ({ used, limit }) => {
  const percent = usedPercent(used, limit);

  return (
    <div className="space-y-1 min-w-[8rem]">
      <div className="flex justify-between text-xs">
        <span className="tabular-nums">{used.toLocaleString()}</span>
        <span className="text-gray-400 tabular-nums">
          {isUnlimited(limit) ? 'Unlimited' : `/ ${limit.toLocaleString()}`}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
        <div
          className={`h-1.5 rounded-full ${fillColor(percent, limit)}`}
          style={{ width: isUnlimited(limit) ? '100%' : `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  );
};

/** Usage with no allowance to measure it against. */
const UsageOnly: React.FC<{ used: number; note?: string }> = ({ used, note }) => (
  <div className="min-w-[8rem]">
    <div className="text-xs tabular-nums text-gray-900">{used.toLocaleString()}</div>
    {note && <div className="text-xs text-gray-400">{note}</div>}
  </div>
);

/**
 * One measure of one row: a bar where an allowance applies, usage alone where none does, and the
 * notes that keep a parent's summed denominator honest.
 */
const MeasureCell: React.FC<{
  row: UsageRow;
  used: number;
  limit?: number;
  subtotalLimit?: number;
  unlimitedChildren: number;
  coverage?: AllowanceSubtotal | null;
}> = ({ row, used, limit, subtotalLimit, unlimitedChildren, coverage }) => {
  const partial = coverage && coverage.organizationsWithAllowance < coverage.organizations;
  const notes = (
    <>
      {unlimitedChildren > 0 && (
        <div className="text-xs text-gray-400">
          +{unlimitedChildren.toLocaleString()} unlimited
        </div>
      )}
      {partial && (
        <div className="text-xs text-gray-500 tabular-nums">
          {coverage!.organizationsWithAllowance} of {coverage!.organizations} with an allowance
        </div>
      )}
    </>
  );

  if (limit !== undefined) return <Allowance used={used} limit={limit} />;

  // Every child unlimited leaves a finite sum of nothing, which a bar would draw as fully consumed.
  if (subtotalLimit !== undefined && !(subtotalLimit === 0 && unlimitedChildren > 0)) {
    return (
      <div className="space-y-1">
        <Allowance used={used} limit={subtotalLimit} />
        {notes}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <UsageOnly
        used={used}
        note={row.state === 'not-recorded' ? NOT_RECORDED
          : unlimitedChildren > 0 ? 'Unlimited'
            : row.kind !== 'organization' && row.kind !== 'provider' ? 'No allowance recorded'
              : undefined}
      />
      {notes}
    </div>
  );
};

type RowKind = 'enterprise' | 'partner' | 'organization' | 'provider';

/** What each level is called, for the mobile card, where indentation cannot carry it. */
const ROW_KIND_LABEL: Record<RowKind, string> = {
  enterprise: 'Enterprise',
  partner: 'Channel partner',
  organization: 'Organization',
  provider: 'Provider',
};

/**
 * Only `measured` draws a bar and only `measured` can be over its allowance, so the bar, the pill
 * and the count above the table cannot disagree.
 */
type RowState = 'measured' | 'not-recorded' | 'no-plan';

interface UsageRow {
  /** `DataTable` infers its row type from this; `getRowId` is what actually keys the rows. */
  id: string;
  kind: RowKind;
  depth: number;
  name: string;
  hasChildren: boolean;
  totals: UsageTotals;
  isDeleted: boolean;
  /** Organization rows only. */
  allowance: PeriodAllowance | null;
  subscription: OrganizationSubscription | null;
  state: RowState | null;
  /** Parent rows only. */
  subtotal: AllowanceSubtotal | null;
  /** Provider rows only. */
  identityResolved: boolean;
  email?: string;
}

/** The plan a row names: the subscription it holds now, else the one its snapshot recorded. */
function planNameOf(row: UsageRow): string | null {
  return row.subscription?.planName ?? row.allowance?.planName ?? null;
}

function organizationState(org: OrganizationRollup): RowState {
  if (org.allowance) return 'measured';
  return org.subscription ? 'not-recorded' : 'no-plan';
}

/**
 * How far into its allowance a row is, as the worst of its two measures. A parent carries its
 * allowance on `subtotal`, where a measure with an unlimited child is unbounded.
 */
function worstPercent(row: UsageRow): number {
  if (row.allowance) {
    return Math.max(
      usedPercent(row.totals.encounters, row.allowance.encounters),
      usedPercent(row.totals.minutes, row.allowance.minutes)
    );
  }
  if (!row.subtotal) return -1;
  return Math.max(
    row.subtotal.unlimitedEncounters > 0
      ? 0
      : usedPercent(row.totals.encounters, row.subtotal.encounters),
    row.subtotal.unlimitedMinutes > 0
      ? 0
      : usedPercent(row.totals.minutes, row.subtotal.minutes)
  );
}

/** Only a row measured against a recorded allowance can be over it. */
function isOverAllowance(row: UsageRow): boolean {
  if (row.state !== 'measured' || !row.allowance) return false;
  const over = (used: number, limit: number) => !isUnlimited(limit) && used > limit;
  return over(row.totals.encounters, row.allowance.encounters)
    || over(row.totals.minutes, row.allowance.minutes);
}

/**
 * Siblings are ordered within their parent, so a sort never flattens the tree. A deactivated
 * organization stays last in either direction.
 */
function sortSiblings(rows: UsageRow[], sort: SortState): UsageRow[] {
  const valueOf = (row: UsageRow): string | number => {
    switch (sort.column) {
      case 'plan': return row.kind === 'organization'
        ? planNameOf(row) ?? ''
        // A parent has no plan of its own; its cell reports how much of it is accounted for.
        : row.subtotal
          ? row.subtotal.organizationsWithAllowance / Math.max(row.subtotal.organizations, 1)
          : -1;
      case 'encounters': return row.totals.encounters;
      case 'minutes': return row.totals.minutes;
      case 'status': return worstPercent(row);
      default: return row.name;
    }
  };

  return [...rows].sort((a, b) => {
    if (a.isDeleted !== b.isDeleted) return a.isDeleted ? 1 : -1;
    if (!sort.column || !sort.direction) return a.name.localeCompare(b.name);

    const left = valueOf(a);
    const right = valueOf(b);
    const cmp = typeof left === 'string'
      ? left.localeCompare(right as string)
      : Number(left) - Number(right);
    return sort.direction === 'asc' ? cmp : -cmp;
  });
}

interface FlattenContext {
  expanded: Record<string, boolean>;
  sort: SortState;
}

function providerRows(org: OrganizationRollup, depth: number, sort: SortState): UsageRow[] {
  const rows = org.providers.map(provider => ({
    id: `provider:${org.organizationId}:${provider.userId}`,
    kind: 'provider' as const,
    depth,
    name: provider.identityResolved ? provider.name ?? provider.email ?? 'Unknown provider'
      : 'Unknown provider',
    hasChildren: false,
    totals: provider.totals,
    isDeleted: false,
    allowance: null,
    subscription: null,
    state: null,
    subtotal: null,
    identityResolved: provider.identityResolved,
    email: provider.email,
  }));

  return sortSiblings(rows, sort);
}

function organizationRows(
  organizations: OrganizationRollup[],
  depth: number,
  context: FlattenContext
): UsageRow[] {
  const rows = organizations.map(org => ({
    id: `org:${org.organizationId}`,
    kind: 'organization' as const,
    depth,
    name: org.name,
    hasChildren: org.providers.length > 0,
    totals: org.totals,
    isDeleted: org.isDeleted,
    allowance: org.allowance,
    subscription: org.subscription,
    state: organizationState(org),
    subtotal: null,
    identityResolved: true,
  }));

  return sortSiblings(rows, context.sort).flatMap(row => {
    const org = organizations.find(o => `org:${o.organizationId}` === row.id)!;
    return context.expanded[row.id]
      ? [row, ...providerRows(org, depth + 1, context.sort)]
      : [row];
  });
}

function partnerRows(
  partners: PartnerRollup[],
  depth: number,
  context: FlattenContext
): UsageRow[] {
  const rows = partners.map(partner => ({
    id: `partner:${partner.partnerId}`,
    kind: 'partner' as const,
    depth,
    name: partner.name,
    hasChildren: partner.organizations.length > 0,
    totals: partner.totals,
    isDeleted: false,
    allowance: null,
    subscription: null,
    state: null,
    subtotal: partner.allowance,
    identityResolved: true,
  }));

  return sortSiblings(rows, context.sort).flatMap(row => {
    const partner = partners.find(p => `partner:${p.partnerId}` === row.id)!;
    return context.expanded[row.id]
      ? [row, ...organizationRows(partner.organizations, depth + 1, context)]
      : [row];
  });
}

function enterpriseRows(
  enterprises: EnterpriseRollup[],
  depth: number,
  context: FlattenContext
): UsageRow[] {
  const rows = enterprises.map(enterprise => ({
    id: `ent:${enterprise.enterpriseId ?? 'none'}`,
    kind: 'enterprise' as const,
    depth,
    name: enterprise.name ?? UNASSIGNED_ENTERPRISE,
    hasChildren: enterprise.partners.length > 0,
    totals: enterprise.totals,
    isDeleted: false,
    allowance: null,
    subscription: null,
    state: null,
    subtotal: enterprise.allowance,
    identityResolved: true,
  }));

  return sortSiblings(rows, context.sort).flatMap(row => {
    const enterprise = enterprises.find(e => `ent:${e.enterpriseId ?? 'none'}` === row.id)!;
    return context.expanded[row.id]
      ? [row, ...partnerRows(enterprise.partners, depth + 1, context)]
      : [row];
  });
}

const allPartners = (report: UsageRollupReport) => report.enterprises.flatMap(e => e.partners);
const allOrganizations = (report: UsageRollupReport) =>
  allPartners(report).flatMap(p => p.organizations);

/**
 * The response is the same shape for every scope, so this is the only place the scope decides
 * anything: partners for an enterprise caller, organizations for a partner caller.
 */
function buildRows(report: UsageRollupReport, context: FlattenContext): UsageRow[] {
  if (report.scope.kind === 'partner') {
    return organizationRows(allOrganizations(report), 0, context);
  }
  if (report.scope.kind === 'enterprise') {
    return partnerRows(allPartners(report), 0, context);
  }
  return enterpriseRows(report.enterprises, 0, context);
}

/** The console dashboard's own metric-to-colour pairings, so Encounters is emerald on both pages. */
const TILE_TINTS: Record<string, string> = {
  emerald: 'bg-emerald-100 text-emerald-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  purple: 'bg-purple-100 text-purple-600',
  violet: 'bg-violet-100 text-violet-600',
};

const DEACTIVATED_KIND_LABEL: Record<DeactivatedRollup['kind'], string> = {
  enterprise: 'Enterprise',
  partner: 'Channel partner',
  organization: 'Organization',
};

/**
 * Deactivated enterprises, partners and organizations, apart from the live tree. Collapsed by
 * default: their usage is in the totals above, and the list is for whoever needs to reconcile it.
 */
const DeactivatedUsage: React.FC<{ entries: DeactivatedRollup[]; totals?: UsageTotals }> = ({ entries, totals }) => (
  <details className="rounded-lg border border-gray-200 bg-white">
    <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700">
      Deactivated ({entries.length})
      {totals && (
        <span className="ml-2 font-normal text-gray-500">
          {totals.encounters.toLocaleString()} encounters · {totals.minutes.toLocaleString()} minutes, included in the totals above
        </span>
      )}
    </summary>
    <div className="overflow-x-auto border-t border-gray-100">
      <table className="min-w-full text-sm" aria-label="Deactivated usage">
        <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th scope="col" className="px-4 py-2">Name</th>
            <th scope="col" className="px-4 py-2">Type</th>
            <th scope="col" className="px-4 py-2">Under</th>
            <th scope="col" className="px-4 py-2 text-right">Organizations</th>
            <th scope="col" className="px-4 py-2 text-right">Encounters</th>
            <th scope="col" className="px-4 py-2 text-right">Minutes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {entries.map(entry => (
            <tr key={`${entry.kind}-${entry.id}`} className="text-gray-700">
              <td className="px-4 py-2 font-medium text-gray-900">{entry.name}</td>
              <td className="px-4 py-2">{DEACTIVATED_KIND_LABEL[entry.kind]}</td>
              <td className="px-4 py-2 text-gray-500">
                {[entry.enterpriseName, entry.partnerName].filter(Boolean).join(' › ') || '—'}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">
                {entry.partners.reduce((n, p) => n + p.organizations.length, 0).toLocaleString()}
              </td>
              <td className="px-4 py-2 text-right tabular-nums">{entry.totals.encounters.toLocaleString()}</td>
              <td className="px-4 py-2 text-right tabular-nums">{entry.totals.minutes.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </details>
);

const SummaryTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  tint: keyof typeof TILE_TINTS;
  hint?: string;
}> = ({ icon, label, value, tint, hint }) => (
  <AnimatedCard hover={false}>
    <div className="p-4">
      <div className={`inline-flex p-1.5 rounded-md ${TILE_TINTS[tint]}`}>{icon}</div>
      <div className="mt-2 text-2xl font-bold text-gray-900 tabular-nums">{value}</div>
      <div className="text-xs font-medium text-gray-500">{label}</div>
      {hint && <div className="mt-0.5 text-xs text-gray-400">{hint}</div>}
    </div>
  </AnimatedCard>
);

/** The colours the console dashboard's hierarchy card gives these levels. */
const LEVEL_MARKS: Record<RowKind, { Icon: LucideIcon; chip: string }> = {
  enterprise: { Icon: Building2, chip: 'bg-indigo-50 text-indigo-600' },
  partner: { Icon: Network, chip: 'bg-violet-50 text-violet-600' },
  organization: { Icon: Store, chip: 'bg-purple-50 text-purple-600' },
  provider: { Icon: User, chip: 'bg-gray-200 text-gray-700' },
};

/** The level is announced as text; the tint is decoration. */
const LevelMark: React.FC<{ kind: RowKind }> = ({ kind }) => {
  const { Icon, chip } = LEVEL_MARKS[kind];

  return (
    <span
      role="img"
      aria-label={ROW_KIND_LABEL[kind]}
      className={`inline-flex items-center justify-center h-6 w-6 rounded-md flex-shrink-0 ${chip}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
  );
};

/** The pill classes for each tone the shared status map defines. */
const TONE_PILL: Record<SubscriptionStatusTone, string> = {
  emerald: 'bg-emerald-100 text-emerald-700',
  blue: 'bg-blue-100 text-blue-700',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-700',
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const tone = SUBSCRIPTION_STATUS_TONES[status];

  return (
    <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${
      tone ? TONE_PILL[tone] : 'bg-gray-100 text-gray-600'
    }`}>
      {SUBSCRIPTION_STATUS_LABELS[status] ?? status}
    </span>
  );
};

/** The row's state, shared by the Status column and the mobile card so the two cannot drift. */
const RowStatus: React.FC<{ row: UsageRow }> = ({ row }) => {
  if (row.kind !== 'organization') return <span className="text-xs text-gray-400">&mdash;</span>;
  if (row.state === 'no-plan') {
    // Stated here rather than left to the Plan column, which is hidden at tablet widths.
    return <span className="text-xs text-gray-500">{NO_SUBSCRIPTION}</span>;
  }
  if (row.state === 'not-recorded') {
    return <span className="text-xs text-gray-500">{NOT_RECORDED}</span>;
  }

  if (isOverAllowance(row)) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
        <AlertTriangle className="h-3 w-3" />
        Over allowance
      </span>
    );
  }

  return worstPercent(row) >= 75 ? (
    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-full">
      <AlertTriangle className="h-3 w-3" />
      Near limit
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
      <TrendingUp className="h-3 w-3" />
      On track
    </span>
  );
};

/**
 * The card shown under 640px. Supplied rather than defaulted for two reasons: the default keeps
 * the first four columns and drops Status, and rendering the name column here would give every
 * disclosure button a second copy of its accessible name, since both views stay mounted.
 */
const UsageCard: React.FC<{ row: UsageRow }> = ({ row }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm space-y-2">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-900 truncate">{row.name}</div>
        <div className="text-xs text-gray-400">{ROW_KIND_LABEL[row.kind]}</div>
      </div>
      {row.isDeleted && (
        <span className="text-xs font-medium text-red-800 bg-red-100 rounded-full px-2 py-0.5 flex-shrink-0">
          Deactivated
        </span>
      )}
    </div>

    {row.kind === 'organization' && (
      <div className="text-xs text-gray-600">{planNameOf(row) ?? NO_SUBSCRIPTION}</div>
    )}

    <div className="grid grid-cols-2 gap-3">
      <div>
        <div className="text-xs text-gray-500">Encounters</div>
        <MeasureCell
          row={row}
          used={row.totals.encounters}
          limit={row.allowance?.encounters}
          subtotalLimit={row.subtotal?.encounters}
          unlimitedChildren={row.subtotal?.unlimitedEncounters ?? 0}
          coverage={row.subtotal}
        />
      </div>
      <div>
        <div className="text-xs text-gray-500">Minutes</div>
        <MeasureCell
          row={row}
          used={row.totals.minutes}
          limit={row.allowance?.minutes}
          subtotalLimit={row.subtotal?.minutes}
          unlimitedChildren={row.subtotal?.unlimitedMinutes ?? 0}
        />
      </div>
    </div>

    <RowStatus row={row} />
  </div>
);

export const ConsoleUsageReport: React.FC = () => {
  const inFlight = useRef<AbortController | null>(null);
  const [report, setReport] = useState<UsageRollupReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sortState, setSortState] = useState<SortState>({ column: 'name', direction: 'asc' });
  const [filters, setFilters] = useState<Filters>({
    periodKey: currentMonthKey(),
    enterpriseId: '',
    partnerId: '',
    organizationId: '',
  });

  /**
   * A filter change starts a new request while the old one is in flight, and the reader would be
   * shown whichever landed last. The abort is also the guard: whoever aborted a request owns the
   * state from then on, including the spinner.
   */
  const fetchReport = useCallback(async () => {
    inFlight.current?.abort();
    const request = new AbortController();
    inFlight.current = request;

    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { periodKey: filters.periodKey };
      if (filters.enterpriseId) params.enterpriseId = filters.enterpriseId;
      if (filters.partnerId) params.partnerId = filters.partnerId;
      if (filters.organizationId) params.organizationId = filters.organizationId;

      const response = await apiService.get<{ success: boolean; data: UsageRollupReport }>(
        '/v1/platform/usage/rollup',
        params,
        { signal: request.signal }
      );
      if (request.signal.aborted) return;
      setReport(response.data);
    } catch (err: any) {
      if (request.signal.aborted) return;
      setError(err.response?.data?.error || 'Failed to load usage');
      setReport(null);
    } finally {
      if (!request.signal.aborted) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  useEffect(() => () => inFlight.current?.abort(), []);

  const scopeKind = report?.scope.kind;

  // The server scopes these, so nothing outside the caller's reach is offered. They come from the
  // scope, not the tree: a dropdown built from the tree loses every sibling as soon as one is
  // chosen.
  const available = report?.available;

  // A deactivated enterprise, partner or organization stays selectable at every level, because its
  // recorded usage still counts toward the totals above it. isDeleted only changes how it reads.
  const enterpriseOptions = useMemo(
    () => (available?.enterprises ?? []).map(e => ({
      id: e.enterpriseId,
      name: e.name,
      isDeleted: e.isDeleted,
    })),
    [available]
  );

  const partnerOptions = useMemo(
    () => (available?.partners ?? [])
      // Narrowed by the chosen enterprise, which is the caller's own narrowing, not the server's.
      .filter(p => !filters.enterpriseId || p.enterpriseId === filters.enterpriseId)
      .map(p => ({ id: p.partnerId, name: p.name, isDeleted: p.isDeleted })),
    [available, filters.enterpriseId]
  );

  const organizationOptions = useMemo(() => {
    const partnersInView = new Set(partnerOptions.map(p => p.id));

    return (available?.organizations ?? [])
      .filter(o => (filters.partnerId ? o.partnerId === filters.partnerId : partnersInView.has(o.partnerId)))
      .map(o => ({ id: o.organizationId, name: o.name, isDeleted: o.isDeleted }));
  }, [available, filters.partnerId, partnerOptions]);

  const organizations = useMemo(
    () => (report ? allOrganizations(report) : []),
    [report]
  );

  const rows = useMemo(
    () => (report ? buildRows(report, { expanded, sort: sortState }) : []),
    [report, expanded, sortState]
  );

  const subscriptionCounts = useMemo(() => {
    const statuses = organizations.map(o => o.subscription?.status).filter(Boolean);
    return {
      active: statuses.filter(s => s === 'active').length,
      trialing: statuses.filter(s => s === 'trialing').length,
    };
  }, [organizations]);

  const overAllowanceCount = useMemo(
    () => organizations.filter(org => isOverAllowance({
      state: organizationState(org),
      allowance: org.allowance,
      totals: org.totals,
    } as UsageRow)).length,
    [organizations]
  );

  const exportCSV = () => {
    if (!report) return;

    // The BOM is what makes Excel on Windows read this as UTF-8 rather than the system codepage.
    const blob = new Blob(['\uFEFF', buildUsageCsv(report)], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `usage-${report.period.key}.csv`;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const setFilter = (key: keyof Filters, value: string) => {
    setFilters(prev => {
      const next = { ...prev, [key]: value };
      // Clearing the parent clears what it scoped, so a stale child cannot be sent with a new
      // parent and be refused.
      if (key === 'enterpriseId') { next.partnerId = ''; next.organizationId = ''; }
      if (key === 'partnerId') next.organizationId = '';
      return next;
    });
  };

  const toggle = (id: string) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const rowBackground = (row: UsageRow) => {
    if (expanded[row.id] && row.hasChildren) return 'bg-purple-50';

    return row.depth > 0 ? 'bg-gray-50/60' : undefined;
  };

  const columns: ColumnDefinition<UsageRow>[] = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      sortKey: 'name',
      isPrimary: true,
      minWidth: '16rem',
      accessor: (row) => (
        <div
          className="flex items-center gap-2 min-w-0"
          style={{ paddingLeft: `${row.depth * 1.25}rem` }}
        >
          {row.hasChildren ? (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); toggle(row.id); }}
              aria-expanded={!!expanded[row.id]}
              aria-label={`${expanded[row.id] ? 'Collapse' : 'Expand'} ${row.name}`}
              className="p-0.5 rounded text-gray-400 hover:bg-purple-50 hover:text-purple-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
            >
              {expanded[row.id]
                ? <ChevronDown className="h-4 w-4" />
                : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <span className="w-5 flex-shrink-0" aria-hidden="true" />
          )}
          <LevelMark kind={row.kind} />
          <span
            className={`truncate ${row.kind === 'provider' ? 'text-sm text-gray-900' : 'text-sm font-medium text-gray-900'}`}
          >
            {row.name}
          </span>
          {row.isDeleted && (
            <span className="text-xs font-medium text-red-800 bg-red-100 rounded-full px-2 py-0.5 flex-shrink-0">
              Deactivated
            </span>
          )}
          {row.kind === 'provider' && !row.identityResolved && (
            <span className="text-xs text-gray-400 flex-shrink-0">identity unknown</span>
          )}
        </div>
      ),
    },
    {
      id: 'plan',
      header: 'Plan',
      sortable: true,
      sortKey: 'plan',
      minWidth: '11rem',
      hideOnTablet: true,
      accessor: (row) => {
        if (row.kind === 'provider') return <span className="text-xs text-gray-400">&mdash;</span>;

        if (row.kind !== 'organization') {
          return (
            <span className="text-xs text-gray-500 tabular-nums">
              {row.subtotal?.organizations ?? 0}
              {row.subtotal?.organizations === 1 ? ' organization' : ' organizations'}
            </span>
          );
        }

        const planName = planNameOf(row);
        if (!planName) {
          return <span className="text-sm text-gray-400">{NO_SUBSCRIPTION}</span>;
        }

        return (
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm text-gray-900 truncate">{planName}</span>
            {row.subscription ? (
              <StatusPill status={row.subscription.status} />
            ) : (
              // The month's snapshot names the plan; no subscription row survives to give a status.
              <span className="px-2 py-0.5 text-xs rounded-full flex-shrink-0 bg-gray-100 text-gray-600">
                ended
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'encounters',
      header: 'Encounters',
      sortable: true,
      sortKey: 'encounters',
      minWidth: '10rem',
      accessor: (row) => (
        <MeasureCell
          row={row}
          used={row.totals.encounters}
          limit={row.allowance?.encounters}
          subtotalLimit={row.subtotal?.encounters}
          unlimitedChildren={row.subtotal?.unlimitedEncounters ?? 0}
          // The coverage caveat rides on this column alone: Plan and Minutes are both hidden at
          // tablet widths, and a partial denominator has to say so wherever the bar is drawn.
          coverage={row.subtotal}
        />
      ),
    },
    {
      id: 'minutes',
      header: 'Minutes',
      sortable: true,
      sortKey: 'minutes',
      minWidth: '10rem',
      hideOnTablet: true,
      accessor: (row) => (
        <MeasureCell
          row={row}
          used={row.totals.minutes}
          limit={row.allowance?.minutes}
          subtotalLimit={row.subtotal?.minutes}
          unlimitedChildren={row.subtotal?.unlimitedMinutes ?? 0}
        />
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      sortKey: 'status',
      align: 'right' as const,
      accessor: (row) => <RowStatus row={row} />,
    },
  ], [expanded]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Usage</h1>
        <p className="text-sm text-gray-500 mt-1">
          Encounters and transcription minutes against what each plan includes, by channel
          partner, organization and provider.
        </p>
      </div>

      {report?.degraded && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">This period is incomplete.</p>
            <p className="mt-1">
              {report.totals.untypedRows.toLocaleString()} usage records predate the field that
              says whether a record is an encounter or transcription minutes. They are counted in
              neither total, so the figures below are understated.
            </p>
          </div>
        </div>
      )}

      {(report?.encountersSpanningBuckets ?? 0) > 0 && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">A parent total is not the sum of its rows here.</p>
            <p className="mt-1">
              {report!.encountersSpanningBuckets!.toLocaleString()} encounters were recorded by more
              than one provider or organization. Each is counted once per row below and once in each
              total above it, so the provider rows add up to more than the figure they sit under.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          {scopeKind === 'platform' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1" htmlFor="usage-enterprise">
                Enterprise
              </label>
              <select
                id="usage-enterprise"
                aria-label="Enterprise"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[12rem]"
                value={filters.enterpriseId}
                onChange={e => setFilter('enterpriseId', e.target.value)}
              >
                <FilterOptions options={enterpriseOptions} allLabel="All enterprises" />
              </select>
            </div>
          )}
          {(scopeKind === 'platform' || scopeKind === 'enterprise') && (
            <div>
              <label className="block text-xs text-gray-500 mb-1" htmlFor="usage-partner">
                Channel partner
              </label>
              <select
                id="usage-partner"
                aria-label="Channel partner"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[12rem]"
                value={filters.partnerId}
                onChange={e => setFilter('partnerId', e.target.value)}
              >
                <FilterOptions options={partnerOptions} allLabel="All channel partners" />
              </select>
            </div>
          )}
          {scopeKind && (
            <div>
              <label className="block text-xs text-gray-500 mb-1" htmlFor="usage-organization">
                Organization
              </label>
              <select
                id="usage-organization"
                aria-label="Organization"
                className="border border-gray-300 rounded-md px-3 py-2 text-sm min-w-[12rem]"
                value={filters.organizationId}
                onChange={e => setFilter('organizationId', e.target.value)}
              >
                <FilterOptions options={organizationOptions} allLabel="All organizations" />
              </select>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1" htmlFor="usage-month">
              Reporting month
            </label>
            <select
              id="usage-month"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={filters.periodKey}
              onChange={e => setFilter('periodKey', e.target.value)}
            >
              {monthOptions().map(m => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={fetchReport}
            className="inline-flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={exportCSV}
            disabled={!report}
            className="inline-flex items-center gap-2 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading && (
        <div className="animate-pulse space-y-3" aria-busy="true">
          <div className="h-20 bg-gray-100 rounded" />
          <div className="h-40 bg-gray-100 rounded" />
        </div>
      )}

      {!loading && report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <SummaryTile
              icon={<Building2 className="h-4 w-4" />}
              label="Organizations"
              value={organizations.length.toLocaleString()}
              tint="violet"
              hint="Matching your filters"
            />
            <SummaryTile
              icon={<CheckCircle2 className="h-4 w-4" />}
              label="Active subscriptions"
              value={subscriptionCounts.active.toLocaleString()}
              tint="purple"
              hint={subscriptionCounts.trialing > 0
                ? `${subscriptionCounts.trialing.toLocaleString()} on trial`
                : undefined}
            />
            <SummaryTile
              icon={<FileText className="h-4 w-4" />}
              label="Encounters"
              value={report.totals.encounters.toLocaleString()}
              tint="emerald"
              hint={(report.deactivatedTotals?.encounters ?? 0) > 0
                ? `Includes ${report.deactivatedTotals!.encounters.toLocaleString()} from deactivated`
                : 'Counted once each'}
            />
            <SummaryTile
              icon={<Clock className="h-4 w-4" />}
              label="Transcription minutes"
              value={report.totals.minutes.toLocaleString()}
              tint="cyan"
              hint={(report.deactivatedTotals?.minutes ?? 0) > 0
                ? `Includes ${report.deactivatedTotals!.minutes.toLocaleString()} from deactivated`
                : undefined}
            />
          </div>

          {overAllowanceCount > 0 && (
            <div
              role="alert"
              className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 p-4"
            >
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">
                  {overAllowanceCount === 1
                    ? '1 organization is over its allowance this month'
                    : `${overAllowanceCount} organizations are over their allowance this month`}
                </p>
                <p className="text-amber-700 mt-1">
                  What that costs is not reported here: no overage amount is recorded against a
                  subscription, so any figure would be zero.
                </p>
              </div>
            </div>
          )}

          <DataTable
            data={rows}
            columns={columns}
            getRowId={(row) => row.id}
            sortable
            sortState={sortState}
            onSortChange={setSortState}
            striped={false}
            rowClassName={(row) => (row.isDeleted ? 'opacity-60' : '')}
            rowBackgroundClass={rowBackground}
            onRowClick={(row) => toggle(row.id)}
            isRowClickable={(row) => row.hasChildren}
            renderMobileCard={(row) => <UsageCard key={row.id} row={row} />}
            ariaLabel="Usage breakdown"
            emptyMessage={`No organizations in your scope for ${monthLabel(filters.periodKey)}.`}
            emptyIcon={<Building2 className="h-12 w-12 text-gray-300" />}
          />

          {(report.deactivated?.length ?? 0) > 0 && (
            <DeactivatedUsage entries={report.deactivated!} totals={report.deactivatedTotals} />
          )}
        </>
      )}
    </div>
  );
};

export default ConsoleUsageReport;
